/**
 * Transaction Operations
 *
 * Handles all transaction-related database operations
 */

import type {
  Transaction,
  TransactionUpdate,
  SplitInfo,
  SaveTransactionsResult,
  BulkUpdateResult,
  SplitTransactionResult,
  UnsplitTransactionResult
} from '../../types';
import { query, queryOne, execute, transaction, saveDB } from '../../db';
import { rowToTransaction, transactionToRow, type TransactionRow } from '../mappers';
import { normalizeDescription, extractMerchant } from '../../analytics/recurring/normalizer';

/**
 * Get all transactions ordered by date (newest first)
 */
export async function getAll(): Promise<Transaction[]> {
    try {
      const rows = query<TransactionRow>('SELECT * FROM transactions ORDER BY date DESC');
      const transactions = rows.map(rowToTransaction);
      return transactions;
    } catch (error) {
      console.error('[transactionOperations] Error loading transactions:', error);
      return [];
    }
  }

/**
 * Save transactions from a CSV file with deduplication
 */
export async function saveForFile(csvFilename: string, transactions: Transaction[]): Promise<SaveTransactionsResult> {
  try {
    let saved = 0;
    let duplicatesSkipped = 0;

    console.log(`[transactionOperations] Saving ${transactions.length} transactions from "${csvFilename}"`);

    // Build a set of dedupeIds that already exist in the database (NOT in the current batch)
    // This allows multiple identical transactions within a single CSV (e.g., same merchant/amount/date)
    // but prevents re-importing previously imported transactions
    const existingDedupeIds = new Set<string>();

    transaction(() => {
      // Pre-fetch all existing dedupeIds that match any transaction in this batch
      const dedupeIdsToCheck = transactions
        .map(t => t.dedupeId)
        .filter((id): id is string => !!id);

      const transactionsWithoutDedupeId = transactions.filter(t => !t.dedupeId);
      if (transactionsWithoutDedupeId.length > 0) {
        console.warn(`[transactionOperations] ⚠️  ${transactionsWithoutDedupeId.length} transactions are missing dedupeId!`,
          transactionsWithoutDedupeId.slice(0, 2).map(t => ({ date: t.date, description: t.description, amount: t.amount }))
        );
      }

      console.log(`[transactionOperations] Checking ${dedupeIdsToCheck.length} dedupeIds (${new Set(dedupeIdsToCheck).size} unique)`);

      if (dedupeIdsToCheck.length > 0) {
        // Use unique dedupeIds for the query (more efficient and avoids SQL parameter limits)
        const uniqueDedupeIds = Array.from(new Set(dedupeIdsToCheck));
        const placeholders = uniqueDedupeIds.map(() => '?').join(',');

        console.log(`[transactionOperations] Querying database for ${uniqueDedupeIds.length} unique dedupeIds...`);

        const existingRows = query<{ dedupe_id: string }>(
          `SELECT dedupe_id FROM transactions WHERE dedupe_id IN (${placeholders})`,
          uniqueDedupeIds
        );
        existingRows.forEach(row => existingDedupeIds.add(row.dedupe_id));

        if (existingDedupeIds.size > 0) {
          console.log(`[transactionOperations] ⚠️  Found ${existingDedupeIds.size} existing dedupeIds in database (will skip these)`);
          console.log(`[transactionOperations] Sample existing dedupeIds:`, Array.from(existingDedupeIds).slice(0, 3));
        } else {
          console.log(`[transactionOperations] ✅ No existing dedupeIds found - all transactions are new`);
        }
      }

      // Track dedupeIds we've seen in THIS batch (to allow duplicates within same CSV)
      // We use a Map to count occurrences: dedupeId -> count
      const batchDedupeIdCount = new Map<string, number>();

      // Now insert transactions, only skipping if dedupeId exists in database
      for (const t of transactions) {
        // Skip if this dedupeId already exists in the database
        if (t.dedupeId && existingDedupeIds.has(t.dedupeId)) {
          duplicatesSkipped++;
          console.log(`[transactionOperations] 🔄 DUPLICATE SKIPPED (already in database): dedupeId="${t.dedupeId}"`, {
            transaction: { date: t.date, description: t.description, amount: t.amount }
          });
          continue;
        } else if (!t.dedupeId) {
          console.warn(`[transactionOperations] ⚠️  Transaction has no dedupeId:`, {
            date: t.date,
            description: t.description,
            amount: t.amount,
            bank: t.bank
          });
        }

        // Track that we've seen this dedupeId in current batch
        if (t.dedupeId) {
          const count = (batchDedupeIdCount.get(t.dedupeId) || 0) + 1;
          batchDedupeIdCount.set(t.dedupeId, count);

          if (count > 1) {
            console.log(`[transactionOperations] ℹ️  Multiple transactions with same dedupeId in batch (${count}x): "${t.description}" - $${t.amount} - Allowing all`);
          }
        }

        // Set file_id to link transaction to source file
        const txnWithFileId = { ...t, fileId: csvFilename };
        insert(txnWithFileId);
        saved++;
      }

      // NOTE: We don't mark file as processed here anymore
      // That's now handled by markFileAsProcessed() in LocalDBProvider
      // which includes hash and metadata
    });

    await saveDB();

    console.log(`[transactionOperations] ✅ Saved ${saved} transactions, skipped ${duplicatesSkipped} duplicates from "${csvFilename}"`);

    return {
      success: true,
      saved,
      duplicatesSkipped
    };
  } catch (error) {
    console.error('[transactionOperations] Error saving transactions:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Insert or update a transaction
 */
export async function upsert(txn: Transaction): Promise<boolean> {
  try {
    transaction(() => {
      const existing = queryOne('SELECT id FROM transactions WHERE id = ?', [txn.id]);

      if (existing) {
        updateById(txn.id, txn);
      } else {
        insert(txn);
      }
    });

    await saveDB();
    return true;
  } catch (error) {
    console.error('[transactionOperations] Error upserting transaction:', error);
    return false;
  }
}

/**
 * Bulk update all transactions with matching description
 */
export async function bulkUpdateByDescription(description: string, updates: TransactionUpdate): Promise<BulkUpdateResult> {
    try {
      // Get transactions to update
      const toUpdate = query<TransactionRow>('SELECT * FROM transactions WHERE description = ?', [description]);

      if (toUpdate.length === 0) {
        return { success: true, updatedCount: 0, previousStates: [] };
      }

      const previousStates = toUpdate.map(rowToTransaction);

      transaction(() => {
        for (const row of toUpdate) {
          const updated: any = { ...row };

          if (updates.category !== undefined) updated.category = updates.category;
          if (updates.person !== undefined) updated.person = updates.person;
          if (updates.displayName !== undefined) updated.display_name = updates.displayName;
          if (updates.isRecurring !== undefined) updated.is_recurring = updates.isRecurring ? 1 : 0;
          if (updates.recurringType !== undefined) updated.recurring_type = updates.recurringType;
          if (updates.excluded !== undefined) updated.excluded = updates.excluded ? 1 : 0;
          if (updates.exclusionReason !== undefined) updated.exclusion_reason = updates.exclusionReason;
          if (updates.memo !== undefined) updated.memo = updates.memo;
          if (updates.categorySource !== undefined) updated.category_source = updates.categorySource;

          updated.updated_at = new Date().toISOString();

          execute(
            `UPDATE transactions SET
              category = ?, person = ?, display_name = ?, is_recurring = ?,
              recurring_type = ?, excluded = ?, exclusion_reason = ?, memo = ?,
              category_source = ?, updated_at = ?
            WHERE id = ?`,
            [
              updated.category,
              updated.person,
              updated.display_name,
              updated.is_recurring,
              updated.recurring_type,
              updated.excluded,
              updated.exclusion_reason,
              updated.memo,
              updated.category_source,
              updated.updated_at,
              updated.id
            ]
          );
        }
      });

      await saveDB();

      return {
        success: true,
        updatedCount: toUpdate.length,
        previousStates
      };
    } catch (error) {
      console.error('[transactionOperations] Error bulk updating transactions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

/**
 * Undo a bulk update by restoring previous states
 */
export async function undoBulkUpdate(previousStates: Transaction[]): Promise<BulkUpdateResult> {
    try {
      transaction(() => {
        for (const txn of previousStates) {
          updateById(txn.id, txn);
        }
      });

      await saveDB();

      return {
        success: true,
        updatedCount: previousStates.length
      };
    } catch (error) {
      console.error('[transactionOperations] Error undoing bulk update:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

/**
 * Split a transaction into multiple parts
 */
export async function split(transactionId: string, splits: SplitInfo[]): Promise<SplitTransactionResult> {
    try {
      const original = queryOne<TransactionRow>('SELECT * FROM transactions WHERE id = ?', [transactionId]);

      if (!original) {
        return {
          success: false,
          error: 'Transaction not found'
        };
      }

      const newTransactions: Transaction[] = [];

      transaction(() => {
        // Update original to mark as split parent
        execute(
          'UPDATE transactions SET is_split = 1, split_children = ? WHERE id = ?',
          [JSON.stringify(splits.map((s) => s.id)), transactionId]
        );

        // Create split children
        for (const split of splits) {
          const splitTxn: Transaction = {
            ...rowToTransaction(original),
            id: split.id,
            amount: split.amount,
            category: split.category,
            person: split.person || original.person,
            displayName: split.displayName || original.display_name,
            splitParentId: transactionId
          };

          insert(splitTxn);
          newTransactions.push(splitTxn);
        }
      });

      await saveDB();

      return {
        success: true,
        originalTransaction: rowToTransaction(original),
        newTransactions
      };
    } catch (error) {
      console.error('[transactionOperations] Error splitting transaction:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

/**
 * Unsplit a transaction (restore original, delete splits)
 * Can be called with either parent ID or child ID
 */
export async function unsplit(transactionId: string): Promise<UnsplitTransactionResult> {
  try {
    let original = queryOne<TransactionRow>('SELECT * FROM transactions WHERE id = ?', [transactionId]);

    if (!original) {
      return {
        success: false,
        error: 'Transaction not found'
      };
    }

    // If this is a split child, find the parent
    if (original.split_parent_id) {
      const parent = queryOne<TransactionRow>('SELECT * FROM transactions WHERE id = ?', [original.split_parent_id]);
      if (parent) {
        original = parent;
        transactionId = parent.id;
      }
    }

    const childIds: string[] = original.split_children ? JSON.parse(original.split_children) : [];

    transaction(() => {
      // Restore original
      execute(
        'UPDATE transactions SET is_split = 0, split_children = NULL WHERE id = ?',
        [transactionId]
      );

      // Delete children
      for (const childId of childIds) {
        execute('DELETE FROM transactions WHERE id = ?', [childId]);
      }
    });

    await saveDB();

    return {
      success: true,
      originalTransaction: rowToTransaction(original),
      removedIds: childIds
    };
  } catch (error) {
    console.error('[transactionOperations] Error unsplitting transaction:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Delete all transactions and processed files
 */
export async function deleteAll(): Promise<boolean> {
  try {
    transaction(() => {
      execute('DELETE FROM transactions');
      execute('DELETE FROM processed_files');
    });

    await saveDB();
    return true;
  } catch (error) {
    console.error('[transactionOperations] Error deleting all transactions:', error);
    return false;
  }
}

/**
 * Delete all transactions from a specific CSV file
 */
export async function deleteFromFile(filename: string): Promise<{ success: boolean; deletedCount: number; error?: string }> {
  try {
    // Get transactions from this file by checking the source field
    const transactionsFromFile = query<TransactionRow>(
      'SELECT * FROM transactions WHERE source = ?',
      [filename]
    );

    const deletedCount = transactionsFromFile.length;

    if (deletedCount === 0) {
      return { success: true, deletedCount: 0 };
    }

    transaction(() => {
      // Delete the transactions
      execute('DELETE FROM transactions WHERE source = ?', [filename]);

      // Delete the processed file record
      execute('DELETE FROM processed_files WHERE filename = ?', [filename]);
    });

    await saveDB();

    return {
      success: true,
      deletedCount
    };
  } catch (error) {
    console.error('[transactionOperations] Error deleting transactions from file:', error);
    return {
      success: false,
      deletedCount: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get transactions with medium confidence for review, grouped by merchant
 * Excludes user-categorized transactions (categorySource = 'user')
 */
export async function getReviewable(): Promise<{
  transactions: Transaction[];
  mediumConfidence: Transaction[];
  uncategorized: Transaction[];
  count: number;
}> {
  try {
    // Get transactions with medium confidence (0.75-0.85) that are not user-categorized
    const mediumConfidenceRows = query<TransactionRow>(
      `SELECT * FROM transactions
       WHERE confidence >= 0.75
       AND confidence < 0.85
       AND category != 'Uncategorized'
       AND category IS NOT NULL
       AND (category_source IS NULL OR category_source != 'user')
       ORDER BY date DESC`
    );

    // Also get uncategorized transactions (potential high-frequency merchants)
    const uncategorizedRows = query<TransactionRow>(
      `SELECT * FROM transactions
       WHERE (category IS NULL OR category = 'Uncategorized')
       AND (category_source IS NULL OR category_source != 'user')
       ORDER BY date DESC`
    );

    const mediumConfidenceTransactions = mediumConfidenceRows.map(rowToTransaction);
    const uncategorizedTransactions = uncategorizedRows.map(rowToTransaction);

    // Combine both sets for grouping
    const allTransactions = [...mediumConfidenceTransactions, ...uncategorizedTransactions];

    // Group transactions by merchant name
    const merchantGroups = new Map<string, Transaction[]>();

    for (const txn of allTransactions) {
      // Normalize description and extract merchant name
      const normalized = normalizeDescription(txn.description);
      const merchant = extractMerchant(normalized);

      // Use primary merchant name as group key, fallback to full normalized description
      const groupKey = merchant.primary || merchant.full || normalized;

      if (!merchantGroups.has(groupKey)) {
        merchantGroups.set(groupKey, []);
      }
      merchantGroups.get(groupKey)!.push(txn);
    }

    // Create representative transactions with grouping metadata
    const mediumConfidenceGroups: Transaction[] = [];
    const uncategorizedGroups: Transaction[] = [];

    for (const [merchantKey, groupTransactions] of merchantGroups.entries()) {
      // Sort by date (newest first) within each group
      groupTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Use the most recent transaction as the representative
      const representative = { ...groupTransactions[0] };

      // Check if this group has uncategorized transactions
      const hasUncategorized = groupTransactions.some(t => !t.category || t.category === 'Uncategorized');

      // For uncategorized groups, only include if there are 5+ transactions
      // For medium-confidence groups, include all
      if (hasUncategorized && groupTransactions.length < 5) {
        continue; // Skip this group - not enough transactions to review
      }

      // Add grouping metadata
      representative._groupCount = groupTransactions.length;
      representative._groupedIds = groupTransactions.map(t => t.id);

      // Separate into medium confidence and uncategorized groups
      if (hasUncategorized) {
        uncategorizedGroups.push(representative);
      } else {
        mediumConfidenceGroups.push(representative);
      }
    }

    // Sort each group by transaction count (descending) to show most frequent first
    mediumConfidenceGroups.sort((a, b) => (b._groupCount || 0) - (a._groupCount || 0));
    uncategorizedGroups.sort((a, b) => (b._groupCount || 0) - (a._groupCount || 0));

    const allGrouped = [...mediumConfidenceGroups, ...uncategorizedGroups];

    return {
      transactions: allGrouped, // Combined for backwards compatibility
      mediumConfidence: mediumConfidenceGroups,
      uncategorized: uncategorizedGroups,
      count: allGrouped.length
    };
  } catch (error) {
    console.error('[transactionOperations] Error loading reviewable transactions:', error);
    return {
      transactions: [],
      mediumConfidence: [],
      uncategorized: [],
      count: 0
    };
  }
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Insert a new transaction
 */
function insert(txn: Transaction): void {
    const row = transactionToRow(txn);
    const now = new Date().toISOString();

    execute(
      `INSERT INTO transactions (
        id, date, description, amount, category, person, source, bank,
        dedupe_id, post_date, type, memo, balance, raw_data, file_id,
        display_name, category_source, confidence, category_method,
        is_recurring, recurring_type, excluded, exclusion_reason, excluded_by, excluded_at,
        is_split, split_parent_id, split_children, created_at, updated_at
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        row.id,
        row.date,
        row.description,
        row.amount,
        row.category,
        row.person,
        row.source,
        row.bank,
        row.dedupe_id,
        row.post_date,
        row.type,
        row.memo,
        row.balance,
        row.raw_data,
        row.file_id || null,
        row.display_name,
        row.category_source,
        row.confidence,
        row.category_method,
        row.is_recurring,
        row.recurring_type,
        row.excluded,
        row.exclusion_reason,
        row.excluded_by,
        row.excluded_at,
        row.is_split,
        row.split_parent_id,
        row.split_children,
        now,
        now
      ]
    );
  }

/**
 * Update an existing transaction
 */
function updateById(id: string, txn: Transaction): void {
    const row = transactionToRow(txn);
    const now = new Date().toISOString();

    execute(
      `UPDATE transactions SET
        date = ?, description = ?, amount = ?, category = ?, person = ?, source = ?, bank = ?,
        dedupe_id = ?, post_date = ?, type = ?, memo = ?, balance = ?, raw_data = ?,
        display_name = ?, category_source = ?, confidence = ?, category_method = ?,
        is_recurring = ?, recurring_type = ?, excluded = ?, exclusion_reason = ?, excluded_by = ?, excluded_at = ?,
        is_split = ?, split_parent_id = ?, split_children = ?, updated_at = ?
      WHERE id = ?`,
      [
        row.date,
        row.description,
        row.amount,
        row.category,
        row.person,
        row.source,
        row.bank,
        row.dedupe_id,
        row.post_date,
        row.type,
        row.memo,
        row.balance,
        row.raw_data,
        row.display_name,
        row.category_source,
        row.confidence,
        row.category_method,
        row.is_recurring,
        row.recurring_type,
        row.excluded,
        row.exclusion_reason,
        row.excluded_by,
        row.excluded_at,
        row.is_split,
        row.split_parent_id,
        row.split_children,
        now,
        id
      ]
    );
}
